import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Building2, User } from "lucide-react";
import BusinessDemoCard from "../components/BusinessDemoCard";
import NoIndex from "../components/NoIndex";
import { BrandLogo } from "../components/BrandLogo";

// Login screens are intentionally separated by audience. The URL param
// `?as=business` switches to the business flow; the default is the customer
// (renter) flow. We deliberately do NOT show a tab toggle anymore: the two
// audiences want very different things and the shared screen was confusing
// (e.g. a customer landing on the business login was a common support ticket).
//
// A small "wrong audience?" link sits at the bottom for users who clicked
// the wrong CTA.
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isBiz = params.get("as") === "business";

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const data = await login(identifier, password, { remember_device: rememberDevice });
      toast.success(`Welcome back, ${data.user.name}!`);
      const at = data.user.account_type || data.user.role;
      // Honor ?next= for deep-links (e.g. Staff Notes installed PWA).
      // Only accept same-origin, in-app paths to avoid open-redirects.
      const nextRaw = params.get("next") || "";
      const safeNext =
        nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//")
          ? nextRaw
          : "";
      if (at === "platform_admin") navigate(safeNext.startsWith("/notepad") ? safeNext : "/admin");
      else if (at === "promoter") navigate("/promoter");
      else if (at === "customer" && data.user.is_support_agent) {
        // Agent-only identity (customer-shaped account created purely via
        // invite-acceptance, no company of their own): sending them to the
        // renter portal would be the exact "dumped into an unrelated
        // customer context" onboarding gap the Support V1 closure audit
        // flagged. They have nowhere else to go, so land them in the
        // workspace directly rather than a portal with no bookings and no
        // visible way to reach it.
        navigate(safeNext.startsWith("/support-workspace") ? safeNext : "/support-workspace/cases");
      }
      else if (at === "customer") navigate(safeNext.startsWith("/portal") ? safeNext : "/portal");
      else navigate((safeNext.startsWith("/app") || safeNext.startsWith("/notepad")) ? safeNext : "/app");
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const Icon = isBiz ? Building2 : User;
  const title = isBiz ? "Business login" : "Customer login";
  const subtitle = isBiz
    ? "Owner & employee access to your business dashboard."
    : "See your bookings, businesses you've used, and notifications.";
  const identifierLabel = isBiz ? "Email or Username" : "Email";
  const identifierPlaceholder = isBiz
    ? "you@business.com or your.username"
    : "you@example.com";
  const submitLabel = loading
    ? "Signing in…"
    : `Sign in${isBiz ? " as Business" : ""}`;
  const switchHelp = isBiz
    ? { text: "Renting a car?", action: "Customer login →", to: "/login" }
    : { text: "Run a business?", action: "Business login →", to: "/login?as=business" };
  const registerPath = isBiz ? "/register?as=business" : "/register";

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <NoIndex title="Sign in · ConfirmPH" />
      <Link to="/" className="flex justify-center mb-6" data-testid="login-brand-link">
        <BrandLogo
          variant="full"
          className="h-16 w-auto"
          alt="ConfirmPH"
          testId="login-brand-logo"
        />
      </Link>
      <div
        className="rounded-2xl border border-slate-200 bg-white p-8"
        data-testid={isBiz ? "login-page-business" : "login-page-user"}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-800">
          <Icon className="w-3.5 h-3.5" /> {isBiz ? "For business owners & staff" : "For renters / customers"}
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold">{title}</h1>
        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form" aria-busy={loading}>
          <div>
            <label htmlFor="login-identifier" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {identifierLabel}
            </label>
            <input
              data-testid="login-identifier-input"
              id="login-identifier"
              type="text"
              required
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={identifierPlaceholder}
              aria-invalid={!!err}
              aria-describedby={err ? "login-error-message" : undefined}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Password</label>
            <input
              data-testid="login-password-input"
              id="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!err}
              aria-describedby={err ? "login-error-message" : undefined}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
            />
          </div>
          {err && (
            <div id="login-error-message" role="alert" data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              data-testid="login-remember-device"
              className="w-4 h-4 rounded border-slate-400 text-emerald-700 focus:ring-emerald-600"
            />
            <span>
              Remember this device for 30 days
            </span>
          </label>
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full min-h-11 px-4 py-3 rounded-lg btn-brand font-medium disabled:opacity-60"
          >
            {submitLabel}
          </button>
        </form>

        <div className="mt-5 text-sm text-slate-600 flex items-center justify-between gap-3">
          <span>
            No account yet?{" "}
            <Link
              to={registerPath}
              className="font-medium text-[#1B4332] hover:underline"
              data-testid="login-go-register-link"
            >
              Create one
            </Link>
          </span>
          <Link
            to="/forgot-password"
            data-testid="login-forgot-password-link"
            className="font-medium text-[#1B4332] hover:underline whitespace-nowrap"
          >
            Forgot password?
          </Link>
        </div>

        {isBiz && <BusinessDemoCard />}

        <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500">
          {switchHelp.text}{" "}
          <Link
            to={switchHelp.to}
            data-testid="login-switch-audience-link"
            className="font-medium text-[#1B4332] hover:underline"
          >
            {switchHelp.action}
          </Link>
        </div>
      </div>
    </div>
  );
}
